import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { CMButton } from '../ui'
import {
  ActionInputField,
  CapabilityAction,
  useMemberActionPreview,
  usePerformMemberAction,
} from '@/services/clubCapabilities'
import { CapabilityFieldInput } from './CapabilityFieldInput'
import { CoachCreditPaywall } from './CoachCreditPaywall'

type FieldValue = string | boolean

export interface ActionMember {
  userId: number
  fullName: string
  /** Current values keyed by input-field key (e.g. { singles, doubles }) for pre-fill + preview. */
  currentValues: Record<string, string | null>
}

type Props = {
  open: boolean
  onClose: () => void
  clubId: number | string
  capabilityKey: string
  action: CapabilityAction
  member: ActionMember
  /** Consumer-facing title override (e.g. "Tune rating" instead of the schema label). */
  titleOverride?: string
  /** Consumer-facing subtitle/description override. */
  descriptionOverride?: string
  /** Submit-button + success-toast label override. */
  submitLabel?: string
  /**
   * Display-only text transform for server-composed strings (verdict message, credit label). Lets a
   * consumer relabel wording — e.g. the coaching surface swaps "adjustment"→"tuning" — without making
   * this generic modal capability-aware. Defaults to identity.
   */
  formatCopy?: (text: string) => string
}

const initialValue = (field: ActionInputField, current: string | null | undefined): FieldValue => {
  if (field.type.kind === 'boolean') return Boolean(field.default ?? false)
  if (current != null && current !== '') return String(current)
  if (field.default != null) return String(field.default)
  return ''
}

const errorMessageFrom = (err: unknown): string => {
  const e = err as {
    response?: { data?: { message?: string; error?: string; errors?: { message?: string }[] } }
    message?: string
  }
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.response?.data?.errors?.[0]?.message ||
    e?.message ||
    'Something went wrong. Please try again.'
  )
}

/**
 * Renders a capability action's typed input fields, pre-filling current values
 * and showing a before→after preview for fields that have one. Fully driven by
 * the action schema — nothing is specific to a single capability.
 */
export const MemberActionModal = ({
  open,
  onClose,
  clubId,
  capabilityKey,
  action,
  member,
  titleOverride,
  descriptionOverride,
  submitLabel,
  formatCopy = (t) => t,
}: Props) => {
  const performAction = usePerformMemberAction()

  const title = titleOverride ?? action.label
  const subtitle = descriptionOverride ?? action.description
  const submitText = submitLabel ?? action.label

  // The values the modal opens with — every input at its current/default. Kept so we can tell what
  // the coach has actually changed (to gate submit) and revert a single field to it.
  const initialValues = useMemo(
    () => Object.fromEntries(action.inputFields.map((f) => [f.key, initialValue(f, member.currentValues[f.key])])),
    [action.inputFields, member.currentValues]
  )
  const [values, setValues] = useState<Record<string, FieldValue>>(initialValues)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [serverError, setServerError] = useState<string | null>(null)

  // Compare two field values the way the coach sees them (booleans as bools, everything else as a
  // scale-preserving string) so "3.500" and "3.5" don't read as different.
  const sameValue = (a: FieldValue | undefined, b: FieldValue | undefined): boolean =>
    typeof a === 'boolean' || typeof b === 'boolean' ? Boolean(a) === Boolean(b) : String(a ?? '') === String(b ?? '')

  // Revert one field back to the value the modal opened with.
  const resetField = (key: string) => setValues((prev) => ({ ...prev, [key]: initialValues[key] }))

  const previewFields = useMemo(
    () => action.inputFields.filter((f) => member.currentValues[f.key] != null && f.type.kind === 'decimal'),
    [action.inputFields, member.currentValues]
  )

  // Nothing to submit until the coach has actually changed something. For a rating adjustment the
  // meaningful change is a rating (a reason edit alone can't be submitted — the action needs a
  // rating), so when the action has decimal inputs we gate on those; otherwise (a capability with
  // no ratings) any field change counts, keeping this generic modal correct for future actions.
  const decimalFields = action.inputFields.filter((f) => f.type.kind === 'decimal')
  const dirtyFields = decimalFields.length > 0 ? decimalFields : action.inputFields
  const isDirty = dirtyFields.some((f) => !sameValue(values[f.key], initialValues[f.key]))

  // Build the wire payload for a given set of fields: booleans as bools, everything else as a
  // scale-preserving string (empty optionals omitted). Parametrized so the same rules apply whether
  // we're building the preview probe (base fields only) or the final submit (base + required extras).
  const buildValues = (fields: ActionInputField[]): Record<string, unknown> => {
    const out: Record<string, unknown> = {}
    for (const field of fields) {
      const raw = values[field.key]
      if (field.type.kind === 'boolean') {
        out[field.key] = Boolean(raw)
        continue
      }
      if (raw === '' || raw == null) continue // omit empty optionals
      // Decimals are sent as strings to preserve trailing-zero scale (e.g. "4.000").
      out[field.key] = raw
    }
    return out
  }

  // Live dry-run: ask the server whether the current input would apply, need approval, or be
  // blocked — so we can warn before submit. The verdict + which extra inputs are required depend
  // only on the base fields (the delta), so we probe with those; the required extras don't change
  // it. Gated to in-range, non-empty values so we don't probe input the server would 400 on.
  const previewPayload = buildValues(action.inputFields)
  const previewEnabled =
    open &&
    Object.keys(previewPayload).length > 0 &&
    action.inputFields.every((field) => {
      const raw = values[field.key]
      if (raw === '' || raw == null) return true
      if (field.type.kind === 'decimal' || field.type.kind === 'int') {
        const num = Number(raw)
        if (Number.isNaN(num)) return false
        if (field.type.min != null && num < field.type.min) return false
        if (field.type.max != null && num > field.type.max) return false
      }
      return true
    })
  const { data: preview } = useMemberActionPreview(
    clubId,
    member.userId,
    capabilityKey,
    action.key,
    previewPayload,
    previewEnabled
  )

  // Requiredness is server-driven and DYNAMIC: the live preview returns the extra inputs THIS
  // change's rule attaches (e.g. a film URL + a written justification). Each carries its OWN
  // required/optional flag — the club sets that per rule — so we render them as-is rather than
  // forcing everything required. When a rule input shares a base field's key (e.g. `reason`), adopt
  // the rule's definition (its label, help text, AND required flag) over the base one so the field
  // reads consistently; otherwise render it as an extra field.
  const ruleInputs = preview?.requiredInputs ?? []
  const ruleByKey = new Map(ruleInputs.map((f) => [f.key, f]))
  const baseKeys = new Set(action.inputFields.map((f) => f.key))
  const renderFields: ActionInputField[] = [
    ...action.inputFields.map((f) => {
      const rule = ruleByKey.get(f.key)
      return rule ? { ...f, ...rule } : f
    }),
    ...ruleInputs.filter((f) => !baseKeys.has(f.key)),
  ]

  const validate = (): boolean => {
    const next: Record<string, string> = {}
    for (const field of renderFields) {
      const raw = values[field.key]
      const isEmpty = raw === '' || raw == null
      if (field.required && isEmpty) {
        next[field.key] = `${field.label} is required.`
        continue
      }
      if (isEmpty) continue
      if (field.type.kind === 'decimal' || field.type.kind === 'int') {
        const num = Number(raw)
        const scale = field.type.kind === 'decimal' ? (field.type.scale ?? 3) : 0
        const decimals = String(raw).split('.')[1]?.length ?? 0
        if (Number.isNaN(num)) next[field.key] = `${field.label} must be a number.`
        else if (field.type.min != null && num < field.type.min)
          next[field.key] = `${field.label} must be at least ${field.type.min}.`
        else if (field.type.max != null && num > field.type.max)
          next[field.key] = `${field.label} must be at most ${field.type.max}.`
        else if (decimals > scale)
          next[field.key] =
            scale === 0
              ? `${field.label} must be a whole number.`
              : `${field.label} allows at most ${scale} decimal places.`
      }
    }
    // Require something to actually submit: when every input is optional, an empty payload is
    // a no-op the server would reject (e.g. coach adjustment needs at least one rating).
    if (Object.keys(next).length === 0 && Object.keys(buildValues(renderFields)).length === 0) {
      next._form = 'Enter at least one value.'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async () => {
    setServerError(null)
    if (preview?.blocked) return
    if (!validate()) return
    try {
      const res = await performAction.mutateAsync({
        clubId,
        userId: member.userId,
        capability: capabilityKey,
        action: action.key,
        values: buildValues(renderFields),
      })
      // An action may be held for admin approval rather than applied immediately — tell the user
      // so a "submitted" outcome isn't mistaken for a completed change. Generic: any action whose
      // response reports PENDING_APPROVAL.
      const pending = (res as { status?: string } | undefined)?.status === 'PENDING_APPROVAL'
      if (pending) toast.success(`${submitText} submitted for admin approval.`)
      // Applied actions (e.g. rating tunes) take effect asynchronously, so the new value won't be
      // reflected instantly — set expectations that it's processing, without leaking how (no queue/
      // SQS wording in user-facing copy).
      else toast.success(`${submitText} is being processed and may take some time to be applied.`)
      onClose()
    } catch (err) {
      setServerError(errorMessageFrom(err))
    }
  }

  // A BLOCKED verdict carries a machine-readable reason: CREDIT (out of credits / needs a
  // subscription) is the only paywall signal; LIMIT (hard cap) and MEMBERSHIP (not adjustable) stay
  // plain error banners.
  const isCreditPaywall = !!preview?.blocked && preview.reasonCode === 'CREDIT'

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50 motion-reduce:transition-none" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-5 text-left align-middle shadow-2xl transition-all">
                <Dialog.Title as="h3" className="text-base font-bold text-gray-900">
                  {title}
                </Dialog.Title>
                <p className="mt-0.5 text-sm text-gray-500">
                  {member.fullName}
                  {subtitle ? ` · ${subtitle}` : ''}
                </p>

                {/* before → after preview (the consequential moment) */}
                {previewFields.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {previewFields.map((field) => {
                      const before = member.currentValues[field.key]
                      const after = values[field.key]
                      const changed = String(after) !== String(before ?? '')
                      return (
                        <div key={field.key} className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[11px] uppercase tracking-wider text-gray-400">{field.label}</p>
                            {changed && (
                              <button
                                type="button"
                                onClick={() => resetField(field.key)}
                                className="text-[11px] font-medium text-gray-400 underline-offset-2 hover:text-primary-700 hover:underline"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-gray-900">
                            <span>{before ?? '—'}</span>
                            <span className="text-gray-400"> → </span>
                            <span className={changed ? 'font-bold text-primary-700' : ''}>
                              {after === '' ? '—' : String(after)}
                            </span>
                          </p>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* dynamic fields — base inputs plus any extra inputs the live preview now requires */}
                <div className="mt-4 flex flex-col gap-y-3">
                  {renderFields.map((field) => (
                    <CapabilityFieldInput
                      key={field.key}
                      field={field}
                      value={values[field.key] ?? ''}
                      onChange={(v) => setValues((prev) => ({ ...prev, [field.key]: v }))}
                      error={errors[field.key]}
                    />
                  ))}
                </div>

                {/* Which credit this change would spend, and how many the coach has left. */}
                {!serverError && !errors._form && !preview?.blocked && preview?.creditRemaining != null && (
                  <p className="mt-3 text-xs text-gray-500">
                    {formatCopy(
                      `${preview.creditRemaining} ${
                        preview.creditTypeLabel?.trim() || preview.creditType?.trim() || 'credits'
                      } left`
                    )}
                  </p>
                )}

                {(serverError || errors._form) && (
                  <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                    {serverError ?? errors._form}
                  </div>
                )}

                {/* Live policy verdict. A credit/subscription block gets an upgrade CTA; any other
                    block is a plain error; held-for-approval is just an amber heads-up. */}
                {!serverError && !errors._form && preview?.blocked && isCreditPaywall && (
                  <CoachCreditPaywall message={formatCopy(preview.message)} />
                )}
                {!serverError && !errors._form && preview?.blocked && !isCreditPaywall && (
                  <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                    {formatCopy(preview.message)}
                  </div>
                )}
                {!serverError && !errors._form && !preview?.blocked && preview?.requiresApproval && (
                  <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    {formatCopy(preview.message)}
                  </div>
                )}

                <div className="mt-5 flex items-center justify-end gap-x-2">
                  <CMButton variant="muted" onClick={onClose}>
                    Cancel
                  </CMButton>
                  <CMButton
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={performAction.isLoading || preview?.blocked || !isDirty}
                  >
                    {performAction.isLoading ? 'Saving…' : submitText}
                  </CMButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
